import { capitalize } from "lodash";
import { dateTimeFormat, relativeTimeFormat } from "./intl";

// adapted from https://github.com/liveblocks/liveblocks/blob/main/packages/liveblocks-react-ui/src/primitives/Timestamp.tsx

const DYNAMIC_DATE_THRESHOLD = 3 * 24 * 60 * 60 * 1000; // 3 days

const relativeUnits = {
  seconds: 60,
  minutes: 60,
  hours: 24,
  days: 7,
  weeks: 4.34524,
  months: 12,
};

// Some locales' relative formatting can be broken (e.g. "-1h") when using the narrow style.
const localesWithBrokenNarrowRelativeFormatting = ["br", "fr", "nb", "nn", "no", "ro", "sv"];

/**
 * Formats a date absolutely with only the day and month.
 */
function formatShortDate(date: Date, locale?: string) {
  const formatter = dateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  });

  return capitalize(formatter.format(date));
}

/**
 * Formats a date relatively with narrow style and auto numeric (for dynamic date switching).
 */
function formatRelativeDateNarrow(date: Date, locale?: string) {
  let resolvedLocale: string;

  if (locale) {
    resolvedLocale = locale;
  } else {
    const formatter = relativeTimeFormat();

    resolvedLocale = formatter.resolvedOptions().locale;
  }

  const isBrokenWhenNarrow = localesWithBrokenNarrowRelativeFormatting.some(
    (locale) => resolvedLocale === locale || resolvedLocale.startsWith(`${locale}-`),
  );

  const formatter = relativeTimeFormat(resolvedLocale, {
    style: isBrokenWhenNarrow ? "short" : "narrow",
    numeric: "auto",
  });

  let difference = (date.getTime() - Date.now()) / 1000;

  if (isJustNow(date)) {
    return formatter.format(0, "seconds");
  }

  for (const [unit, length] of Object.entries(relativeUnits)) {
    if (Math.abs(difference) < length) {
      return formatter.format(Math.round(difference), unit as Intl.RelativeTimeFormatUnit);
    }

    difference /= length;
  }

  return capitalize(formatter.format(Math.round(difference), "years"));
}

function ensureUTC(date: string | number | Date) {
  if (typeof date === "string") {
    // Check if the date already ends with "Z" or with a UTC offset like "+00:00" or "-00:00"
    const utcOffsetRegex = /([+-]00:00)$/;
    if (!date.endsWith("Z") && !utcOffsetRegex.test(date)) {
      date += "Z";
    }
  }
  return date;
}

/**
 * True when the date falls within the sub-minute window that formatDynamicDate
 * renders as "now" — so callers can substitute their own label (e.g. "just now").
 */
export const isJustNow = (date: string | number | Date) => {
  const difference = (new Date(ensureUTC(date)).getTime() - Date.now()) / 1000;
  return difference > -relativeUnits.seconds && difference < relativeUnits.seconds;
};

/**
 * Formats a date relatively if it's recent,
 * otherwise absolutely with only the day and month.
 */
export const formatDynamicDate = (date: string | number | Date, locale?: string) => {
  const parsedDate = new Date(ensureUTC(date));
  return parsedDate.getTime() > Date.now() - DYNAMIC_DATE_THRESHOLD
    ? formatRelativeDateNarrow(parsedDate, locale)
    : formatShortDate(parsedDate, locale);
};

/**
 * Formats a date relatively regardless of age ("2 days ago", "3 weeks ago",
 * "1 month ago"). Long style, unlike formatDynamicDate's narrow style and
 * 3-day absolute-date fallback. Sub-minute ages render as "now".
 */
export const formatRelativeDate = (date: string | number | Date, locale?: string) => {
  const parsedDate = new Date(ensureUTC(date));
  let difference = (parsedDate.getTime() - Date.now()) / 1000;

  if (difference > -relativeUnits.seconds && difference < relativeUnits.seconds) {
    // numeric:"auto" here so fresh timestamps read "now", not "in 0 seconds".
    return relativeTimeFormat(locale, { style: "long", numeric: "auto" }).format(0, "seconds");
  }

  const formatter = relativeTimeFormat(locale, { style: "long", numeric: "always" });

  for (const [unit, length] of Object.entries(relativeUnits)) {
    if (Math.abs(difference) < length) {
      return formatter.format(Math.round(difference), unit as Intl.RelativeTimeFormatUnit);
    }

    difference /= length;
  }

  return formatter.format(Math.round(difference), "years");
};
